import axios from "axios";
import SvgGoogleLogo from "../assets/google_logo";
import { BACKEND_URL } from "../config";
import { TokenResponse, useGoogleLogin } from "@react-oauth/google";
import { Link, Navigate } from "react-router-dom";
import { useState } from "react";
import { SpinningSvg } from "../components/spinner";

const Login = () => {
  const token = localStorage.getItem("auth_token");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    username: "",
    password: "",
    non_field_errors: "",
  });
  const logInWithAccessToken = async (
    accessToken: string,
    profile_info: any
  ) => {
    const response = await axios.post(
      BACKEND_URL + "/api/auth/google/login/",
      {
        access_token: accessToken,
        profile: profile_info,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    const jwt_token = response.data;
    setJwtTokenAndRedirect(jwt_token);
    return jwt_token;
  };

  const setJwtTokenAndRedirect = (jwt_token: string) => {
    localStorage.clear();
    localStorage.setItem("auth_token", jwt_token);
    window.location.href = "/app/websites/";
  };

  const onFormSubmit = async (event: any) => {
    event.preventDefault();
    setLoading(true);
    const formData = new FormData(event.target);
    const data = Object.fromEntries(formData.entries());
    const response = await axios
      .post(BACKEND_URL + "/api/auth/", data, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        setJwtTokenAndRedirect(response.data.token);
      })
      .catch((error) => {
        setErrors(error.response.data);
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onGoogleSuccess = (
    tokenResponse: Omit<
      TokenResponse,
      "error" | "error_description" | "error_uri"
    >
  ) => {
    setLoading(true);
    checkTokenInfo(tokenResponse);
  };

  const onError = (error: any) => {
    console.log("login failed:", error);
  };

  const googleLogin = useGoogleLogin({
    onSuccess: onGoogleSuccess,
    onError,
  });

  if (token) {
    return <Navigate to="/app/websites/" />;
  }

  const checkTokenInfo = async (tokenInfo: TokenResponse) => {
    if (tokenInfo) {
      axios
        .get(
          `https://www.googleapis.com/oauth2/v1/userinfo?alt=json&access_token=${tokenInfo["access_token"]}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${tokenInfo["access_token"]}`,
            },
          }
        )
        .then(
          async (response) => {
            const newAuthToken = await logInWithAccessToken(
              tokenInfo["access_token"],
              response["data"]
            );
          },
          (error) => {
            console.log(error);
          }
        )
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-gray-100 px-4">
      <div className="mx-auto max-w-md w-full space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight">
            Log in to your account
          </h1>
          <p className="mt-2 text-gray-500">
            Don't have an account?{" "}
            <Link
              className="font-medium text-gray-900 hover:underline"
              to="/register"
            >
              Register
            </Link>
          </p>
        </div>
        <div className="space-y-4">
          <button
            className="login-button inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary hover:bg-primary/90 h-10 px-4 py-2 w-full hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2"
            onClick={() => googleLogin()}
          >
            <div style={{ display: "flex" }}>
              <div
                style={{ height: 18, marginRight: 8, minWidth: 18, width: 18 }}
              >
                <SvgGoogleLogo width={20} height={20} />
              </div>
              <div
                style={{
                  lineHeight: "18px",
                  fontFamily: '"Google Sans", arial, sans-serif',
                  fontWeight: 500,
                }}
              >
                Log in with Google
              </div>
            </div>
          </button>
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-gray-100 px-2 text-gray-500">
                Or continue with
              </span>
            </div>
          </div>
          <form className="space-y-4" onSubmit={onFormSubmit}>
            <div>
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Email address
              </label>
              <input
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                id="username"
                name="username"
                placeholder="name@example.com"
                type="email"
              />
              {errors.username && (
                <p className="text-red-500 ">{errors.username}</p>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Password
                </label>
                <a
                  className="text-sm font-medium text-gray-900 hover:underline"
                  href="#"
                >
                  Forgot password?
                </a>
              </div>
              <input
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                id="password"
                name="password"
                placeholder="••••••••"
                type="password"
              />

              {errors.password && (
                <p className="text-red-500 ">{errors.password}</p>
              )}
            </div>

            {errors.non_field_errors && (
              <p className="text-red-500 ">{errors.non_field_errors}</p>
            )}

            <button
              className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary hover:bg-primary/90 h-10 px-4 py-2 w-full bg-gradient-to-r from-[rgb(255,111,72)] to-[rgb(240,42,166)] text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2"
              type="submit"
              disabled={loading}
            >
              Log in {loading && <SpinningSvg />}
            </button>
            <Link to="/">
              <button
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary hover:bg-primary/90 h-10 px-4 py-2 w-full text-black hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2"
                type="submit"
                disabled={loading}
              >
                Back
              </button>
            </Link>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
